import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
  Color,
  ConeGeometry,
  CylinderGeometry,
  type MeshBasicMaterial,
  type Mesh,
  QuadraticBezierCurve3,
  Quaternion,
  Vector3,
} from 'three';
import type { Graph3DPositionedNode } from '../../core/graph.types';
import type { Graph3DEdgeRendererProps } from '../../core/renderer.types';
import { getDepthRenderOrder } from '../renderOrder';

const up = new Vector3(0, 1, 0);
const DEFAULT_COLOR = '#0f172a';
const BASE_RADIUS = 0.28;
const ACTIVE_RADIUS = 0.36;
const EDGE_LIFT_Y = 2.4;
const MIN_CURVE_OFFSET = 4;
const MAX_CURVE_OFFSET = 14;
const MIN_CURVE_LIFT = 1.2;
const MAX_CURVE_LIFT = 4.2;
const ARROW_RADIUS = 1.2;
const ARROW_LENGTH = 4;
const EDGE_RENDER_ORDER_OFFSET = 60;
const FLOW_HEAD_SPEED = 1.05;
const FLOW_TRAIL_SPAN = 0.26;
const FLOW_WAKE_SPAN = 0.58;
const FLOW_BASE_OPACITY = 0.46;
const FLOW_PEAK_OPACITY_BOOST = 0.42;
const FLOW_SEGMENT_MAX_LENGTH = 3.4;
const STATIC_CURVE_POINT_MIN = 5;
const STATIC_CURVE_POINT_MAX = 12;
const EDGE_SEGMENT_DEBUG_PREFIX = '[Graph3D edge perf]';
const EDGE_CYLINDER_GEOMETRY = new CylinderGeometry(1, 1, 1, 10);
const EDGE_ARROW_GEOMETRY = new ConeGeometry(1, 1, 12);

function shouldLogEdgeSegmentPerf() {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    (window as Window & { __GRAPH3D_EDGE_SEGMENT_DEBUG__?: boolean })
      .__GRAPH3D_EDGE_SEGMENT_DEBUG__ === true
  );
}

function getGraph3DPerfContext() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (
    window as Window & { __GRAPH3D_PERF_CONTEXT__?: Record<string, unknown> }
  ).__GRAPH3D_PERF_CONTEXT__;
}

function getEdgeDataString(data: unknown, key: string, fallback = '') {
  if (!data || typeof data !== 'object') {
    return fallback;
  }
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : fallback;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getNodeCenter(node: Graph3DPositionedNode): Vector3 {
  return new Vector3(
    node.resolvedPosition.x,
    node.resolvedPosition.y,
    node.resolvedPosition.z
  );
}

function getNodeBoundaryPoint(
  node: Graph3DPositionedNode,
  toward: Vector3
): Vector3 {
  const center = getNodeCenter(node);
  const direction = toward.clone().sub(center);
  if (direction.lengthSq() === 0) {
    return center;
  }
  const halfWidth = Math.max(node.resolvedSize.width / 2, 1);
  const halfDepth = Math.max(
    (node.resolvedSize.depth ?? node.resolvedSize.height) / 2,
    1
  );
  const ratio = Math.max(
    Math.abs(direction.x) / halfWidth,
    Math.abs(direction.z) / halfDepth,
    0.001
  );
  return new Vector3(
    center.x + direction.x * (1 / ratio),
    center.y,
    center.z + direction.z * (1 / ratio)
  );
}

function createSegment(source: Vector3, target: Vector3) {
  const direction = target.clone().sub(source);
  const length = direction.length();
  if (length < 0.8) {
    return null;
  }
  return {
    source: source.clone(),
    target: target.clone(),
    midpoint: source.clone().add(target).multiplyScalar(0.5),
    length,
    quaternion: new Quaternion().setFromUnitVectors(up, direction.normalize()),
  };
}

function clampRevealProgress(progress: number | undefined) {
  if (typeof progress !== 'number') {
    return 1;
  }
  return Math.min(Math.max(progress, 0), 1);
}

function createFlowSegments(source: Vector3, target: Vector3) {
  const direction = target.clone().sub(source);
  const length = direction.length();
  if (length < 0.8) {
    return [] as Array<NonNullable<ReturnType<typeof createSegment>>>;
  }
  const segmentCount = Math.max(1, Math.ceil(length / FLOW_SEGMENT_MAX_LENGTH));
  const segments = [] as Array<NonNullable<ReturnType<typeof createSegment>>>;
  for (let index = 0; index < segmentCount; index += 1) {
    const startPoint = source.clone().lerp(target, index / segmentCount);
    const endPoint = source.clone().lerp(target, (index + 1) / segmentCount);
    const segment = createSegment(startPoint, endPoint);
    if (segment) {
      segments.push(segment);
    }
  }
  return segments;
}

function getEdgeHashSign(edgeId: string) {
  const hash = Array.from(edgeId).reduce(
    (sum, char) => sum + char.charCodeAt(0),
    0
  );
  return hash % 2 === 0 ? 1 : -1;
}

function getEdgePhase(edgeId: string) {
  const hash = Array.from(edgeId).reduce(
    (sum, char) => sum + char.charCodeAt(0),
    0
  );
  return (hash % 100) / 100;
}

function createCurvePoints(
  sourceNode: Graph3DPositionedNode,
  targetNode: Graph3DPositionedNode,
  edgeId: string,
  detailed: boolean
) {
  const sourceCenter = getNodeCenter(sourceNode);
  const targetCenter = getNodeCenter(targetNode);
  const start = getNodeBoundaryPoint(sourceNode, targetCenter);
  const end = getNodeBoundaryPoint(targetNode, sourceCenter);
  start.y += EDGE_LIFT_Y;
  end.y += EDGE_LIFT_Y;

  const planarDirection = new Vector3(end.x - start.x, 0, end.z - start.z);
  const planarLength = planarDirection.length();
  const safePlanarDirection =
    planarLength < 0.001
      ? new Vector3(1, 0, 0)
      : planarDirection.clone().normalize();
  const perpendicular = new Vector3(
    -safePlanarDirection.z,
    0,
    safePlanarDirection.x
  );
  const curveOffset =
    clamp(planarLength * 0.18, MIN_CURVE_OFFSET, MAX_CURVE_OFFSET) *
    getEdgeHashSign(edgeId);
  const curveLift = clamp(planarLength * 0.08, MIN_CURVE_LIFT, MAX_CURVE_LIFT);
  const control = start
    .clone()
    .add(end)
    .multiplyScalar(0.5)
    .add(perpendicular.multiplyScalar(curveOffset));
  control.y += curveLift;

  const curve = new QuadraticBezierCurve3(start, control, end);
  const pointCount = detailed
    ? Math.max(12, Math.min(28, Math.round(planarLength / 2.2)))
    : Math.max(
        STATIC_CURVE_POINT_MIN,
        Math.min(STATIC_CURVE_POINT_MAX, Math.round(planarLength / 6.8))
      );
  return curve.getPoints(pointCount);
}

export function ClusterMockArrowEdge3D({
  edge,
  runtime,
  theme,
}: Graph3DEdgeRendererProps) {
  const edgeColor = getEdgeDataString(edge.data, 'themeColor', DEFAULT_COLOR);
  const isFlowing = runtime?.flow === true && runtime?.hidden !== true;
  const color =
    runtime?.color ??
    (runtime?.active || runtime?.highlighted
      ? edgeColor
      : edgeColor || theme.edge.color);
  const points = useMemo(
    () =>
      createCurvePoints(edge.sourceNode, edge.targetNode, edge.id, isFlowing),
    [edge.id, edge.sourceNode, edge.targetNode, isFlowing]
  );
  const segments = useMemo(() => {
    const lines = [] as Array<NonNullable<ReturnType<typeof createSegment>>>;
    for (let index = 0; index < points.length - 1; index += 1) {
      if (isFlowing) {
        lines.push(...createFlowSegments(points[index], points[index + 1]));
        continue;
      }
      const segment = createSegment(points[index], points[index + 1]);
      if (segment) {
        lines.push(segment);
      }
    }
    return lines;
  }, [isFlowing, points]);
  const segmentProgresses = useMemo(() => {
    const totalLength = segments.reduce(
      (sum, segment) => sum + segment.length,
      0
    );
    if (totalLength <= 0) {
      return segments.map(() => 0);
    }
    let elapsedLength = 0;
    return segments.map(segment => {
      const progress = (elapsedLength + segment.length / 2) / totalLength;
      elapsedLength += segment.length;
      return progress;
    });
  }, [segments]);
  const segmentRanges = useMemo(() => {
    const totalLength = segments.reduce(
      (sum, segment) => sum + segment.length,
      0
    );
    if (totalLength <= 0) {
      return segments.map(() => ({ start: 0, end: 0 }));
    }
    let elapsedLength = 0;
    return segments.map(segment => {
      const start = elapsedLength / totalLength;
      elapsedLength += segment.length;
      return {
        start,
        end: elapsedLength / totalLength,
      };
    });
  }, [segments]);
  const targetPoint = points[points.length - 1] ?? new Vector3();
  const previousPoint = points[points.length - 2] ?? targetPoint;
  const targetDirection = targetPoint.clone().sub(previousPoint);
  const targetQuaternion = useMemo(
    () =>
      new Quaternion().setFromUnitVectors(
        up,
        targetDirection.lengthSq() === 0 ? up : targetDirection.normalize()
      ),
    [targetDirection]
  );
  const radius =
    runtime?.active || runtime?.highlighted ? ACTIVE_RADIUS : BASE_RADIUS;
  const materialRefs = useRef<Array<MeshBasicMaterial | null>>([]);
  const arrowMaterialRef = useRef<MeshBasicMaterial | null>(null);
  const arrowMeshRef = useRef<Mesh | null>(null);
  const edgePhase = useMemo(() => getEdgePhase(edge.id), [edge.id]);
  const renderOrder = getDepthRenderOrder(
    edge.sourceNode.depth ?? edge.targetNode.depth,
    EDGE_RENDER_ORDER_OFFSET
  );
  const revealProgress = clampRevealProgress(runtime?.progress);
  const opacity = runtime?.hidden ? 0.12 : 1;
  const flowColor = useMemo(
    () => new Color(color).lerp(new Color('#7dd3fc'), 0.46),
    [color]
  );
  const flowHotColor = useMemo(
    () => flowColor.clone().lerp(new Color('#ffffff'), 0.82),
    [flowColor]
  );

  useEffect(() => {
    if (!shouldLogEdgeSegmentPerf()) {
      return;
    }
    console.info(EDGE_SEGMENT_DEBUG_PREFIX, 'cluster-mock-segments', {
      edgeId: edge.id,
      pointCount: points.length,
      segmentCount: segments.length,
      flowEnabled: isFlowing,
      hidden: runtime?.hidden === true,
      ...getGraph3DPerfContext(),
    });
  }, [edge.id, isFlowing, points.length, runtime?.hidden, segments.length]);

  useFrame(({ clock }) => {
    if (!runtime?.flow || runtime?.hidden || segmentProgresses.length === 0) {
      if (arrowMeshRef.current) {
        if (!runtime?.hidden && revealProgress >= 0.999) {
          arrowMeshRef.current.scale.set(
            ARROW_RADIUS,
            ARROW_LENGTH,
            ARROW_RADIUS
          );
        } else {
          arrowMeshRef.current.scale.set(0.0001, 0.0001, 0.0001);
        }
      }
      return;
    }
    const head = (clock.elapsedTime * FLOW_HEAD_SPEED + edgePhase) % 1;
    materialRefs.current.forEach((material, index) => {
      if (!material) {
        return;
      }
      const progress = segmentProgresses[index] ?? 0;
      const distance = head >= progress ? head - progress : head - progress + 1;
      const pulse =
        distance <= FLOW_TRAIL_SPAN ? 1 - distance / FLOW_TRAIL_SPAN : 0;
      const wake =
        distance <= FLOW_WAKE_SPAN ? 1 - distance / FLOW_WAKE_SPAN : 0;
      const blend = Math.min(1, pulse + wake * 0.5);
      material.color.copy(flowColor).lerp(flowHotColor, blend);
      material.opacity = Math.min(
        1,
        FLOW_BASE_OPACITY + pulse * FLOW_PEAK_OPACITY_BOOST + wake * 0.14
      );
    });
    if (arrowMaterialRef.current) {
      const arrowPulse = head >= 0.78 ? (head - 0.78) / 0.22 : 0;
      arrowMaterialRef.current.color
        .copy(flowColor)
        .lerp(flowHotColor, Math.min(1, arrowPulse + 0.24));
      arrowMaterialRef.current.opacity = Math.min(
        1,
        FLOW_BASE_OPACITY + arrowPulse * 0.68
      );
    }
    if (arrowMeshRef.current) {
      const arrowPulse = head >= 0.78 ? (head - 0.78) / 0.22 : 0;
      arrowMeshRef.current.scale.setScalar(1 + arrowPulse * 0.22);
    }
  });

  if (segments.length === 0) {
    return null;
  }

  return (
    <group>
      {segments.map((segment, index) => {
        const range = segmentRanges[index] ?? { start: 0, end: 1 };
        if (!runtime?.flow && revealProgress <= range.start) {
          return null;
        }
        let segmentLength = segment.length;
        let segmentPosition = segment.midpoint;
        if (!runtime?.flow && revealProgress < range.end) {
          const localProgress =
            range.end > range.start
              ? (revealProgress - range.start) / (range.end - range.start)
              : 0;
          const clampedLocalProgress = clamp(localProgress, 0, 1);
          const direction = segment.target
            .clone()
            .sub(segment.source)
            .normalize();
          segmentLength = Math.max(
            segment.length * clampedLocalProgress,
            0.001
          );
          segmentPosition = segment.source
            .clone()
            .add(direction.multiplyScalar(segmentLength / 2));
        }
        return (
          <mesh
            key={`${edge.id}-${index}`}
            position={segmentPosition}
            quaternion={segment.quaternion}
            renderOrder={renderOrder}
            scale={[radius, segmentLength, radius]}
          >
            <primitive object={EDGE_CYLINDER_GEOMETRY} attach="geometry" />
            <meshBasicMaterial
              ref={material => {
                materialRefs.current[index] = material;
              }}
              color={runtime?.flow && !runtime?.hidden ? flowColor : color}
              transparent
              opacity={
                runtime?.flow && !runtime?.hidden ? FLOW_BASE_OPACITY : opacity
              }
              depthTest={false}
              depthWrite={false}
            />
          </mesh>
        );
      })}
      <mesh
        ref={arrowMeshRef}
        position={targetPoint}
        quaternion={targetQuaternion}
        renderOrder={renderOrder}
        scale={
          runtime?.flow || revealProgress >= 0.999
            ? [ARROW_RADIUS, ARROW_LENGTH, ARROW_RADIUS]
            : [0.0001, 0.0001, 0.0001]
        }
      >
        <primitive object={EDGE_ARROW_GEOMETRY} attach="geometry" />
        <meshBasicMaterial
          ref={arrowMaterialRef}
          color={runtime?.flow && !runtime?.hidden ? flowColor : color}
          transparent
          opacity={
            runtime?.flow && !runtime?.hidden ? FLOW_BASE_OPACITY : opacity
          }
          depthTest={false}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}
